import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Divider,
  Text,
  Loader,
  Button,
  Stack,
  Card,
  Badge,
  Group,
  Avatar,
  Grid,
  Title,
  Table,
  SegmentedControl,
  Paper,
  Image,
  Select,
  Modal,
  Checkbox,
  Tabs,
  Tooltip as MantineTooltip,
  Anchor,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useMemo, useState, useCallback, useRef } from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import html2canvas from 'html2canvas';
import { CSVLink } from 'react-csv';
import {
  Document,
  Page,
  Text as PdfText,
  View,
  StyleSheet,
  pdf,
  Link,
  Image as PdfImage,
} from '@react-pdf/renderer';
import { pipe, map, filter, reduce, omit } from 'remeda';
import { LineChart, BarChart } from '@mantine/charts';
import { Calendar, Download, Filter, Info, ArrowRight } from 'lucide-react';
import {
  formatTimestampToTime,
  formatTimestampToDateTimeWithMs,
  formatTimestampToTimeWithMs,
} from '../../../utils/formatDuration';
import {
  formatItalianSessionDateTime,
  formatSessionDurationDetailed,
} from '../../../utils/sessionDisplayDates';
import { getDeviceImageSrc } from '../../../utils/deviceImage';
import type { SessionAnswer } from '../../../utils/sessionAnswers';
import {
  parseSessionAnswersJsonToRows,
  type SessionAnswersContext,
} from '../../../utils/parseSessionAnswersFromStorage';
import { getEffectiveHeartRateData } from '../../../utils/hrDataRepair';
import { getExposureItemStats } from '../../../utils/exposureFoodStats';
import type { Phase, Device } from '@types';
import { useConfigurationQuery } from '../../../hooks/queries/useUserConfigurationsQuery';
import { useAuthUser } from '@store/useAuthStore';
import { useUserDevices } from '../../../hooks/useUserDevices';
import type {
  DeviceConfigurationApi,
  HeartRateEvent,
} from '@model/DeviceConfiguration';
import { EXPOSURE_EVENTS_CONFIG_KEY } from '@model/DeviceConfiguration';
import { getAppConfig } from '@configs/apps/appConfigs';
import { getScenarioDisplayLabel } from '../../../data/maps';
import { getFoodNamesByIds } from '../../../data/foods';
import { getExposureConfigFromSession } from '../../../utils/extractSessionObject';
import { translateExposureEvents } from '../../../utils/translateExposureConfig';
import { BodyImageCompletedSessionView } from './BodyImageCompletedSessionView';
import { getSessionTypeDisplay } from '../utils/sessionTypeDisplay';
import { USER_ROUTES } from '../../../routes/userRoutes';
import sessionInfoCardClasses from '../../SessionDetails/_components/SessionInfoCard/SessionInfoCard.module.css';
import {
  findPortataNumber,
  formatExposureItemEventDetail,
  formatExposureItemStatLabel,
  getExposureItemEventTypeLabel,
  getPortateFromExposureConfig,
  type ExposureFoodDisplayContext,
} from '../../../utils/exposureMultiItem';
// ============================================================================
// Types
// ============================================================================

interface SessionMetrics {
  baseline: number;
  min: number;
  max: number;
  avg: number;
  hrv: number;
}

interface EventCategoryCount {
  'p-2': number;
  'p-1': number;
  p0: number;
  'p+1': number;
  'p+2': number;
}

interface SessionDates {
  start: string;
  end: string;
  date: string;
}

type FeedbackAtPoint = {
  itemIds: number[];
  score: number;
  timestamp: number;
};

interface ChartDataPoint {
  time: string; // Formattato per display
  hr: number;
  timestamp: number; // Timestamp originale per filtraggio
  feedback?: FeedbackAtPoint[];
}

interface PatientSectionProps {
  session: DeviceConfigurationApi;
  patientCode: string;
}

interface SessionSectionProps {
  session: DeviceConfigurationApi;
  duration: string;
}

interface HeartRateChartSectionProps {
  chartData: ChartDataPoint[];
  metrics: SessionMetrics;
  baseline: number;
  onTimeIntervalChange: (start: Date | null, end: Date | null) => void;
  timeIntervalStart: Date | null;
  timeIntervalEnd: Date | null;
  /** Conteggio fasi elevate (P+1 + P+2) da session.results */
  tachycardiaEpisodes: number;
}

interface EventsTableProps {
  events: HeartRateEvent[];
  phaseEvents?: Array<{
    timestamp: number;
    phase: Phase;
    hr: number;
  }>;
}

interface DevicesSectionProps {
  devices: Device[];
}

interface EventCategoriesChartProps {
  categoryCounts: EventCategoryCount;
}

// ============================================================================
// Helper Functions
// ============================================================================

const calculateMetrics = (
  events: HeartRateEvent[],
  baseline: number
): SessionMetrics => {
  if (events.length === 0) {
    return {
      baseline,
      min: 0,
      max: 0,
      avg: 0,
      hrv: 0,
    };
  }

  const hrValues = pipe(
    events,
    map(e => e.hr),
    filter((hr): hr is number => typeof hr === 'number' && hr > 0)
  );

  const hrvValues = pipe(
    events,
    map(e => e.hrv),
    filter((hrv): hrv is number => typeof hrv === 'number' && hrv > 0)
  );

  const min = hrValues.length > 0 ? Math.min(...hrValues) : 0;
  const max = hrValues.length > 0 ? Math.max(...hrValues) : 0;
  const avg =
    hrValues.length > 0
      ? Math.round(reduce(hrValues, (acc, hr) => acc + hr, 0) / hrValues.length)
      : 0;
  const hrv =
    hrvValues.length > 0
      ? Math.round(
          reduce(hrvValues, (acc, hrv) => acc + hrv, 0) / hrvValues.length
        )
      : 0;

  return {
    baseline,
    min,
    max,
    avg,
    hrv,
  };
};

const countEventsByState = (
  results: DeviceConfigurationApi['results']
): EventCategoryCount => {
  // Gestisce il caso in cui results sia una stringa JSON
  let parsedResults: Array<{ phase?: string; state?: string }> = [];

  if (!results) {
    return { 'p-2': 0, 'p-1': 0, p0: 0, 'p+1': 0, 'p+2': 0 };
  }

  // Se results è una stringa, parsala
  if (typeof results === 'string') {
    try {
      parsedResults = JSON.parse(results);
    } catch (e) {
      console.error('Error parsing results:', e);
      return { 'p-2': 0, 'p-1': 0, p0: 0, 'p+1': 0, 'p+2': 0 };
    }
  } else if (Array.isArray(results)) {
    parsedResults = results;
  } else {
    return { 'p-2': 0, 'p-1': 0, p0: 0, 'p+1': 0, 'p+2': 0 };
  }

  // Normalizza lo stato: gestisce sia 'p2' che 'p+2', 'p1' che 'p+1', etc.
  const normalizeState = (
    state: string | undefined
  ): keyof EventCategoryCount | null => {
    if (!state) return null;

    const normalized = state.toLowerCase().trim();

    // Gestisce i casi con e senza segno +
    if (normalized === 'p-2') return 'p-2';
    if (normalized === 'p-1') return 'p-1';
    if (normalized === 'p0' || normalized === 'p 0') return 'p0';
    if (normalized === 'p+1') return 'p+1';
    if (normalized === 'p+2') return 'p+2';
    // Nel DB "p2" corrisponde a "p+2" e "p1" a "p+1" (basato sul contesto)
    if (normalized === 'p2') {
      return 'p+2';
    }
    if (normalized === 'p1') {
      return 'p+1';
    }

    return null;
  };

  return pipe(
    parsedResults,
    map(result => {
      // Usa 'phase' se presente (come nel DB), altrimenti 'state' come fallback
      const phaseValue = result.phase || result.state;
      return normalizeState(phaseValue);
    }),
    filter((state): state is keyof EventCategoryCount => state !== null),
    reduce(
      (acc, state) => {
        acc[state] += 1;
        return acc;
      },
      { 'p-2': 0, 'p-1': 0, p0: 0, 'p+1': 0, 'p+2': 0 } as EventCategoryCount
    )
  );
};

const formatSessionDuration = (start: string, end: string): string =>
  formatSessionDurationDetailed(start, end);

const getPatientInitials = (patientCode: string): string => {
  const parts = patientCode.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return patientCode.substring(0, 2).toUpperCase();
};

const getPhaseEventsFromSession = (
  session: DeviceConfigurationApi
): Array<{ timestamp: number; phase: Phase; hr: number }> => {
  // Gestisce il caso in cui results sia una stringa JSON
  let parsedResults: Array<{
    date?: string;
    timestamp?: number | string;
    state?: string;
    phase?: string;
    hr?: number;
  }> = [];

  if (!session.results) {
    return [];
  }

  // Se results è una stringa, parsala
  if (typeof session.results === 'string') {
    try {
      parsedResults = JSON.parse(session.results);
    } catch (e) {
      console.error('Error parsing results:', e);
      return [];
    }
  } else if (Array.isArray(session.results)) {
    parsedResults = session.results;
  } else {
    return [];
  }

  // Normalizza lo stato: gestisce sia 'p2' che 'p+2', 'p1' che 'p+1', etc.
  const normalizeState = (state: string | undefined): Phase | null => {
    if (!state) return null;

    const normalized = state.toLowerCase().trim();

    // Gestisce i casi con e senza segno +
    if (normalized === 'p-2') return 'p-2';
    if (normalized === 'p-1') return 'p-1';
    if (normalized === 'p0' || normalized === 'p 0') return 'p0';
    if (normalized === 'p+1') return 'p+1';
    if (normalized === 'p+2') return 'p+2';
    // Nel DB "p2" corrisponde a "p+2" e "p1" a "p+1"
    if (normalized === 'p2') return 'p+2';
    if (normalized === 'p1') return 'p+1';

    return null;
  };

  return pipe(
    parsedResults,
    map(
      (result: {
        date?: string;
        timestamp?: number | string;
        state?: string;
        phase?: string;
        hr?: number;
      }) => {
        // Usa 'phase' se presente, altrimenti 'state' come fallback
        const phaseValue = result.phase || result.state;
        const normalizedPhase = normalizeState(phaseValue);

        if (!normalizedPhase) return null;

        // Gestisce sia 'date' che 'timestamp' per il timestamp
        let timestamp: number;
        if (result.timestamp) {
          timestamp =
            typeof result.timestamp === 'number'
              ? result.timestamp
              : new Date(result.timestamp).getTime();
        } else if (result.date) {
          timestamp = new Date(result.date).getTime();
        } else {
          return null;
        }

        // Verifica che il timestamp sia valido
        if (Number.isNaN(timestamp)) {
          return null;
        }

        return {
          timestamp,
          phase: normalizedPhase,
          hr: result.hr || 0,
        };
      }
    ),
    filter((event): event is NonNullable<typeof event> => event !== null)
  );
};

// ============================================================================
// CSV Data Preparation Functions
// ============================================================================

const prepareDevicesCsvData = (devices: Device[]) => {
  return devices.map(device => ({
    ID: device.id || 'N/A',
    Nome: device.name || 'N/A',
    Codice: device.code || 'N/A',
    Tipo: device.type || 'N/A',
    Modello: device.model || 'N/A',
    'Client ID': device.clientId || 'N/A',
    'Collegato a': device.connected_to || 'N/A',
  }));
};

const preparePatientCsvData = (session: DeviceConfigurationApi) => {
  return [
    {
      'Codice Utente': session.patientCode || 'N/A',
      'ID Utente': session.patient_id || 'N/A',
      'ID Sessione': session.id || 'N/A',
    },
  ];
};

const prepareSessionCsvData = (
  session: DeviceConfigurationApi,
  duration: string
) => {
  const scenarioName = getScenarioDisplayLabel(
    session.type,
    session.scenario,
    session.video?.name
  );
  const appTypeLabel =
    getSessionTypeDisplay(session.type, session.scenario).productLabel ||
    session.type ||
    'N/A';
  const variation = session.config?.variation || 0;

  return [
    {
      'ID Sessione': session.id || 'N/A',
      'Tipo App': appTypeLabel,
      'Scenario/Esperienza': scenarioName || 'N/A',
      Baseline: session.baseline || 0,
      Variazione: variation > 0 ? `${variation}%` : 'N/A',
      Durata: duration,
      'Data Creazione': session.created_at
        ? new Date(session.created_at).toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : 'N/A',
      'Data Aggiornamento': session.updated_at
        ? new Date(session.updated_at).toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : 'N/A',
      Stato: 'Completata',
    },
  ];
};

const prepareEventsCsvData = (events: HeartRateEvent[]) => {
  return events.map(event => ({
    Timestamp: event.timestamp || 0,
    'Data/Ora': event.timestamp
      ? formatTimestampToDateTimeWithMs(event.timestamp)
      : 'N/A',
    'Heart Rate (bpm)': event.hr || 0,
    'HRV (ms)': event.hrv || 'N/A',
    LF: event.lf || 'N/A',
    HF: event.hf || 'N/A',
    'Device Code': event.deviceCode || 'N/A',
  }));
};

const preparePhasesCsvData = (
  phaseEvents: Array<{ timestamp: number; phase: Phase; hr: number }>
) => {
  return phaseEvents.map(event => ({
    Timestamp: event.timestamp || 0,
    'Data/Ora': event.timestamp
      ? formatTimestampToDateTimeWithMs(event.timestamp)
      : 'N/A',
    Fase: event.phase || 'N/A',
    'Heart Rate (bpm)': event.hr || 0,
  }));
};

const formatAnswerTimestampForCsv = (timestamp: string | number) => {
  if (timestamp === '' || timestamp === null || timestamp === undefined) {
    return 'N/A';
  }
  try {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return String(timestamp);
    }
    return date.toLocaleString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
  } catch {
    return String(timestamp);
  }
};

const prepareAnswersCsvData = (
  answers: string | null,
  session: SessionAnswersContext
) => {
  if (!answers || answers.trim() === '') {
    return [{ Messaggio: 'Nessuna risposta disponibile' }];
  }

  const rows = parseSessionAnswersJsonToRows(answers, session);
  if (rows.length === 0) {
    return [{ Messaggio: 'Nessuna risposta disponibile' }];
  }

  return rows.map(row => ({
    Orario: formatAnswerTimestampForCsv(row.timestamp),
    Domanda: row.question,
    Risposta: row.answer,
    'Step ID': row.stepId,
  }));
};

type SerializedExposureEvent = {
  type: string;
  timestamp: number;
  itemIds?: number[];
  score?: number;
  clientId?: string;
};

const prepareExposureEventsCsvData = (
  events: SerializedExposureEvent[],
  display: ExposureFoodDisplayContext = { isMultiItem: false, portate: [] }
): Array<Record<string, string | number>> => {
  const formatTs = (ts: number) => formatTimestampToDateTimeWithMs(ts);
  const typeLabel = (e: SerializedExposureEvent) => {
    if (e.type === 'itemShown' || e.type === 'itemFeedback') {
      return getExposureItemEventTypeLabel(e.type, display.isMultiItem);
    }
    if (e.type === 'tutorialCompleted') return 'Tutorial completato';
    if (e.type === 'sessionStartedAt') return 'Inizio sessione';
    if (e.type === 'sessionEndedAt') return 'Fine sessione';
    if (e.type === 'mealEnded') return 'Pasto terminato';
    return e.type;
  };
  const detail = (e: SerializedExposureEvent) => {
    const itemIds = Array.isArray(e.itemIds) ? e.itemIds : [];
    if (e.type === 'itemShown' || e.type === 'itemFeedback') {
      const portataNumber = display.isMultiItem
        ? findPortataNumber(display.portate, itemIds)
        : null;
      return formatExposureItemEventDetail({
        eventType: e.type,
        itemIds,
        portataNumber,
        isMultiItem: display.isMultiItem,
        score: e.type === 'itemFeedback' ? e.score : undefined,
      });
    }
    if (e.type === 'mealEnded') return e.clientId ?? '—';
    return '—';
  };
  return [...events]
    .sort((a, b) => a.timestamp - b.timestamp)
    .map(e => ({
      Timestamp: e.timestamp,
      'Data/Ora': formatTs(e.timestamp),
      Tipo: typeLabel(e),
      Dettaglio: detail(e),
    }));
};

type ExposureItemStatRow = {
  itemIds: number[];
  score: number;
  viewTimeMs: number;
  hrAtFeedback: number | null;
};

const prepareExposureItemStatsCsvData = (
  stats: ExposureItemStatRow[],
  display: ExposureFoodDisplayContext
): Array<Record<string, string | number>> => {
  const label = display.isMultiItem ? 'Gruppo' : 'Elemento';
  return stats.map(s => ({
    [label]: formatExposureItemStatLabel({
      itemIds: s.itemIds,
      portataNumber: display.isMultiItem
        ? findPortataNumber(display.portate, s.itemIds)
        : null,
      isMultiItem: display.isMultiItem,
    }),
    'Score ansia (1–5)': s.score,
    'Tempo visualizzazione cibo (s)': (s.viewTimeMs / 1000).toFixed(1),
    'HR al feedback (bpm)': s.hrAtFeedback ?? '—',
  }));
};

// ============================================================================
// Components
// ============================================================================

const PatientSection = ({ session, patientCode }: PatientSectionProps) => {
  const initials = getPatientInitials(patientCode);
  const patientId = session.patient_id;
  const canLinkToUser =
    Boolean(patientCode?.trim()) &&
    typeof patientId === 'number' &&
    patientId > 0;

  return (
    <Card shadow='sm' withBorder p='md' radius='sm'>
      <Stack gap='xs' mb='md'>
        <Title order={4}>Utente</Title>
        <Text size='xs' c='dimmed'>
          Informazioni anagrafiche e tecnico referente
        </Text>
      </Stack>
      <Group gap='md' align='flex-start' justify='space-between' wrap='nowrap'>
        <Group gap='md' align='center'>
          <Avatar size={50} radius='xl' color='violet'>
            {initials}
          </Avatar>
          <Stack gap={4}>
            {canLinkToUser ? (
              <Anchor
                component={RouterLink}
                to={USER_ROUTES.detail(patientId)}
                className={sessionInfoCardClasses.userLink}
                underline='never'
                aria-label={`Vai al dettaglio utente ${patientCode}`}
              >
                <Text
                  fw={600}
                  size='lg'
                  component='span'
                  className={sessionInfoCardClasses.userLinkName}
                >
                  {patientCode}
                </Text>
                <span className={sessionInfoCardClasses.userLinkHint}>
                  <ArrowRight size={14} aria-hidden />
                  <Text component='span' size='xs' fw={500}>
                    Vai al dettaglio
                  </Text>
                </span>
              </Anchor>
            ) : (
              <Text fw={600} size='lg' style={{ whiteSpace: 'nowrap' }}>
                {patientCode}
              </Text>
            )}
            <Text size='sm' c='dimmed'>
              ID utente #{patientId || session.id}
            </Text>
          </Stack>
        </Group>
        <Stack gap={4} align='flex-end'>
          {/* <Text size='sm' c='dimmed'>
            Tecnico referente:
          </Text>
          <Text size='sm'>[Nome Tecnico]</Text> */}
        </Stack>
      </Group>
    </Card>
  );
};

const SessionSection = ({ session, duration }: SessionSectionProps) => {
  const variation = session.config?.variation || 0;
  const hasVariation = getAppConfig(session?.type ?? 'default').hasVariation;
  const scenarioName = getScenarioDisplayLabel(
    session.type,
    session.scenario,
    session.video?.name
  );
  const appTypeLabel =
    getSessionTypeDisplay(session.type, session.scenario).productLabel ||
    session.type ||
    'N/A';

  return (
    <Card shadow='sm' withBorder p='md' radius='sm'>
      <Stack gap='xs' mb='md'>
        <Title order={4}>Sessione</Title>
        <Text size='xs' c='dimmed'>
          Parametri di acquisizione
        </Text>
      </Stack>
      <Grid gutter='md'>
        <Grid.Col span={6}>
          <Text size='xs' c='dimmed' mb={4}>
            Scenario/Esperienza:
          </Text>
          <Text fw={500}>{scenarioName}</Text>
        </Grid.Col>
        <Grid.Col span={6}>
          <Text size='xs' c='dimmed' mb={4}>
            Tipo app:
          </Text>
          <Text fw={500}>{appTypeLabel}</Text>
        </Grid.Col>
        {hasVariation && (
          <Grid.Col span={6}>
            <Text size='xs' c='dimmed' mb={4}>
              Variazione:
            </Text>
            <Text fw={500}>{variation > 0 ? `${variation}%` : 'N/A'}</Text>
          </Grid.Col>
        )}
        <Grid.Col span={6}>
          <Text size='xs' c='dimmed' mb={4}>
            Durata:
          </Text>
          <Text fw={500}>{duration}</Text>
          {session.started_at && (
            <Text size='xs' c='dimmed' mt={4}>
              Inizio: {formatItalianSessionDateTime(session.started_at)}
            </Text>
          )}
          {session.ended_at && (
            <Text size='xs' c='dimmed' mt={2}>
              Fine: {formatItalianSessionDateTime(session.ended_at)}
            </Text>
          )}
        </Grid.Col>
      </Grid>
    </Card>
  );
};

const HeartRateChartSection = ({
  chartData,
  metrics,
  baseline,
  onTimeIntervalChange,
  timeIntervalStart,
  timeIntervalEnd,
  tachycardiaEpisodes,
}: HeartRateChartSectionProps) => {
  // Prepara i timestamp unici ordinati per i Select
  const availableTimestamps = useMemo(() => {
    if (chartData.length === 0) return [];
    // Estrai timestamp unici e ordina
    const uniqueTimestamps = pipe(
      chartData,
      map(point => point.timestamp),
      arr => [...new Set(arr)],
      arr => arr.sort((a, b) => a - b)
    );
    return uniqueTimestamps;
  }, [chartData]);

  // Opzioni per il Select Inizio (tutti i timestamp tranne l'ultimo)
  const startTimestampOptions = useMemo(() => {
    if (availableTimestamps.length < 2) return [];
    // Prendi tutti tranne l'ultimo
    return availableTimestamps.slice(0, -1).map(timestamp => ({
      value: timestamp.toString(),
      label: new Date(timestamp).toLocaleTimeString('it-IT', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    }));
  }, [availableTimestamps]);

  // Opzioni per il Select Fine (solo timestamp dopo quello selezionato come inizio)
  const endTimestampOptions = useMemo(() => {
    if (!timeIntervalStart || availableTimestamps.length === 0) {
      // Se non c'è inizio selezionato, mostra tutti tranne il primo
      return availableTimestamps.slice(1).map(timestamp => ({
        value: timestamp.toString(),
        label: new Date(timestamp).toLocaleTimeString('it-IT', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
      }));
    }

    const startTime = timeIntervalStart.getTime();
    // Filtra solo i timestamp dopo l'inizio selezionato
    return availableTimestamps
      .filter(timestamp => timestamp > startTime)
      .map(timestamp => ({
        value: timestamp.toString(),
        label: new Date(timestamp).toLocaleTimeString('it-IT', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
      }));
  }, [availableTimestamps, timeIntervalStart]);

  // Filtra i dati in base all'intervallo selezionato
  const filteredChartData = useMemo(() => {
    if (!timeIntervalStart || !timeIntervalEnd) {
      return chartData;
    }

    const startTime = timeIntervalStart.getTime();
    const endTime = timeIntervalEnd.getTime();

    return pipe(
      chartData,
      filter(point => {
        return point.timestamp >= startTime && point.timestamp <= endTime;
      })
    );
  }, [chartData, timeIntervalStart, timeIntervalEnd]);

  const dataToDisplay =
    filteredChartData.length > 0 ? filteredChartData : chartData;

  return (
    <Card shadow='sm' withBorder p='md' radius='sm'>
      <Stack gap='xs' mb='md'>
        <Title order={4}>Andamento frequenza cardiaca</Title>
        <Text size='xs' c='dimmed'>
          bpm nel tempo · min / max / baseline
        </Text>
      </Stack>

      {/* Controlli per l'intervallo */}
      <Stack gap='sm' mb='md'>
        <Group gap='md' align='flex-end' wrap='wrap'>
          <Select
            label='Inizio'
            placeholder='Seleziona timestamp inizio'
            data={startTimestampOptions}
            value={
              timeIntervalStart ? timeIntervalStart.getTime().toString() : null
            }
            onChange={(value: string | null) => {
              if (!value) {
                onTimeIntervalChange(null, null);
                return;
              }
              const timestamp = parseInt(value, 10);
              const newStart = new Date(timestamp);

              // Se la fine è minore o uguale all'inizio, aggiorna la fine al timestamp successivo
              if (
                timeIntervalEnd &&
                newStart.getTime() >= timeIntervalEnd.getTime()
              ) {
                const nextTimestamp = availableTimestamps.find(
                  t => t > newStart.getTime()
                );
                if (nextTimestamp) {
                  onTimeIntervalChange(newStart, new Date(nextTimestamp));
                } else {
                  // Se non c'è un timestamp successivo, usa l'ultimo
                  const lastTimestamp =
                    availableTimestamps[availableTimestamps.length - 1];
                  if (lastTimestamp) {
                    onTimeIntervalChange(newStart, new Date(lastTimestamp));
                  }
                }
              } else {
                onTimeIntervalChange(newStart, timeIntervalEnd);
              }
            }}
            clearable
            style={{ minWidth: '200px' }}
            size='sm'
          />
          <Select
            label='Fine'
            placeholder='Seleziona timestamp fine'
            data={endTimestampOptions}
            value={
              timeIntervalEnd ? timeIntervalEnd.getTime().toString() : null
            }
            onChange={(value: string | null) => {
              if (!value) {
                onTimeIntervalChange(timeIntervalStart, null);
                return;
              }
              const timestamp = parseInt(value, 10);
              const newEnd = new Date(timestamp);

              // Valida che la fine sia maggiore dell'inizio
              if (
                timeIntervalStart &&
                newEnd.getTime() <= timeIntervalStart.getTime()
              ) {
                // Se è minore o uguale, imposta al timestamp successivo all'inizio
                const nextTimestamp = availableTimestamps.find(
                  t => t > timeIntervalStart.getTime()
                );
                if (nextTimestamp) {
                  onTimeIntervalChange(
                    timeIntervalStart,
                    new Date(nextTimestamp)
                  );
                } else {
                  const lastTimestamp =
                    availableTimestamps[availableTimestamps.length - 1];
                  if (lastTimestamp) {
                    onTimeIntervalChange(
                      timeIntervalStart,
                      new Date(lastTimestamp)
                    );
                  }
                }
                return;
              }

              onTimeIntervalChange(timeIntervalStart, newEnd);
            }}
            clearable
            disabled={!timeIntervalStart}
            style={{ minWidth: '200px' }}
            size='sm'
          />
          <Button
            variant='light'
            size='sm'
            onClick={() => {
              onTimeIntervalChange(null, null);
            }}
            disabled={!timeIntervalStart && !timeIntervalEnd}
          >
            Reset
          </Button>
        </Group>
        {timeIntervalStart && timeIntervalEnd && (
          <Text size='xs' c='dimmed'>
            Intervallo:{' '}
            {timeIntervalStart.toLocaleTimeString('it-IT', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}{' '}
            -{' '}
            {timeIntervalEnd.toLocaleTimeString('it-IT', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
            {availableTimestamps.length > 0 && (
              <span>
                {' '}
                (range:{' '}
                {new Date(availableTimestamps[0]).toLocaleTimeString('it-IT', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}{' '}
                -{' '}
                {new Date(
                  availableTimestamps[availableTimestamps.length - 1]
                ).toLocaleTimeString('it-IT', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
                )
              </span>
            )}
          </Text>
        )}
      </Stack>

      <div style={{ height: '300px', marginBottom: '1rem' }}>
        {dataToDisplay.length > 0 ? (
          <LineChart
            h={300}
            data={dataToDisplay}
            dataKey='time'
            series={[{ name: 'hr', color: 'blue', label: 'HR (bpm)' }]}
            curveType='natural'
            withDots={false}
            withLegend={false}
            referenceLines={[
              {
                y: baseline,
                color: 'yellow',
                label: `Baseline ${baseline} bpm`,
              },
            ]}
            lineProps={series =>
              series.name === 'hr'
                ? {
                    dot: (props: {
                      cx?: number;
                      cy?: number;
                      payload?: ChartDataPoint;
                    }) =>
                      props.payload?.feedback?.length ? (
                        <circle
                          cx={props.cx}
                          cy={props.cy}
                          r={6}
                          fill='var(--mantine-color-violet-6)'
                          stroke='white'
                          strokeWidth={2}
                        />
                      ) : (
                        <g />
                      ),
                  }
                : {}
            }
            tooltipProps={{
              content: ({
                active,
                payload,
                label,
              }: {
                active?: boolean;
                payload?: Array<{
                  payload?: ChartDataPoint;
                  name?: string;
                  value?: unknown;
                }>;
                label?: string;
              }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0]?.payload;
                const feedbacks = p?.feedback ?? [];
                return (
                  <Stack
                    gap={4}
                    p='xs'
                    style={{
                      background: 'var(--mantine-color-default)',
                      color: 'var(--mantine-color-text)',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  >
                    <Text size='xs' fw={500}>
                      {label}
                    </Text>
                    {payload.map((item, idx) => (
                      <Text key={item.name ?? idx} size='xs' component='span'>
                        {item.name != null
                          ? `${item.name}: ${item.value ?? ''}`
                          : null}
                      </Text>
                    ))}
                    {feedbacks.length > 0 && (
                      <>
                        <Text size='xs' fw={600} mt={4} mb={2}>
                          Feedback cibo
                        </Text>
                        {feedbacks.map((fb, i) => (
                          <Text key={i} size='xs' component='span'>
                            {(fb.itemIds?.length ?? 0) > 0
                              ? getFoodNamesByIds(fb.itemIds ?? [])
                              : '—'}{' '}
                            — score: {fb.score},{' '}
                            {formatTimestampToTime(fb.timestamp)}
                          </Text>
                        ))}
                      </>
                    )}
                  </Stack>
                );
              },
            }}
          />
        ) : (
          <Stack gap='xs' align='center' mt='xl' px='md'>
            <Text c='dimmed' ta='center' fw={500}>
              {timeIntervalStart && timeIntervalEnd
                ? "Nessun dato disponibile per l'intervallo selezionato"
                : 'Nessun dato di frequenza cardiaca disponibile'}
            </Text>
          </Stack>
        )}
      </div>
      <Grid gutter='md'>
        <Grid.Col span={3}>
          <Paper p='xs' withBorder>
            <Text size='xs' c='dimmed' mb={4}>
              Baseline HR
            </Text>
            <Text fw={600} size='lg'>
              {metrics.baseline} bpm
            </Text>
            <Text size='xs' c='dimmed'>
              +2 vs precedente
            </Text>
          </Paper>
        </Grid.Col>
        <Grid.Col span={3}>
          <Paper p='xs' withBorder>
            <Text size='xs' c='dimmed' mb={4}>
              Minima
            </Text>
            <Text fw={600} size='lg'>
              {metrics.min} bpm
            </Text>
            <Text size='xs' c='dimmed'>
              -6 vs precedente
            </Text>
          </Paper>
        </Grid.Col>
        <Grid.Col span={3}>
          <Paper p='xs' withBorder>
            <Text size='xs' c='dimmed' mb={4}>
              Massima
            </Text>
            <Text fw={600} size='lg'>
              {metrics.max} bpm
            </Text>
            <MantineTooltip
              label='Conteggio dei cambi di fase P+1 e P+2 nella sessione (HR sopra la baseline). Coincide con il grafico Eventi per Fasi.'
              multiline
              w={280}
              withArrow
            >
              <Text size='xs' c='dimmed' style={{ cursor: 'help' }}>
                {tachycardiaEpisodes}{' '}
                {tachycardiaEpisodes === 1
                  ? 'episodio tachicardia'
                  : 'episodi tachicardia'}{' '}
                (P+1/P+2)
              </Text>
            </MantineTooltip>
          </Paper>
        </Grid.Col>
        <Grid.Col span={3}>
          <Paper p='xs' withBorder>
            <Text size='xs' c='dimmed' mb={4}>
              Media sessione
            </Text>
            <Text fw={600} size='lg'>
              {metrics.avg} bpm
            </Text>
            <Text size='xs' c='dimmed'>
              HRV {metrics.hrv} ms
            </Text>
          </Paper>
        </Grid.Col>
      </Grid>
    </Card>
  );
};

const EventsTable = ({ events, phaseEvents = [] }: EventsTableProps) => {
  const [filter, setFilter] = useState<'all' | 'heartRate' | 'stateChanges'>(
    'all'
  );

  const getStateColor = useCallback((state: Phase): string => {
    switch (state) {
      case 'p0':
        return 'gray';
      case 'p1':
        return 'blue';
      case 'p2':
        return 'red';
      case 'p-1':
        return 'cyan';
      case 'p-2':
        return 'purple';
      default:
        return 'gray';
    }
  }, []);

  const combinedData = useMemo(() => {
    const data: Array<{
      timestamp: number;
      type: 'heartRate' | 'stateChange' | 'answer';
      phase?: Phase;
      hr?: number;
      hrv?: number;
      lf?: number;
      hf?: number;
      question?: string;
      answer?: string;
    }> = [];

    if (filter === 'all' || filter === 'heartRate') {
      events.forEach(point => {
        const timestamp = point.timestamp || Date.now();
        data.push({
          timestamp,
          type: 'heartRate',
          hr: point.hr,
          hrv: point.hrv ?? undefined,
          lf: point.lf ?? undefined,
          hf: point.hf ?? undefined,
        });
      });
    }

    if (filter === 'all' || filter === 'stateChanges') {
      phaseEvents.forEach(change => {
        data.push({
          timestamp: change.timestamp,
          type: 'stateChange',
          phase: change.phase,
          hr: change.hr || 0,
        });
      });
    }

    return pipe(data, arr =>
      [...arr].sort((a, b) => a.timestamp - b.timestamp)
    );
  }, [events, phaseEvents, filter]);

  const totalCount = useMemo(() => {
    const heartRateLength = events.length;
    const stateChangesLength = phaseEvents.length;

    switch (filter) {
      case 'heartRate':
        return heartRateLength;
      case 'stateChanges':
        return stateChangesLength;
      default:
        return heartRateLength + stateChangesLength;
    }
  }, [events, phaseEvents, filter]);

  return (
    <Card shadow='sm' withBorder p='md' radius='sm'>
      <Stack gap='xs' mb='md'>
        <Title order={4}>Eventi</Title>
        <Text size='xs' c='dimmed'>
          Timeline dettagliata episodi
        </Text>
      </Stack>

      <Stack gap='md'>
        <Group justify='space-between' align='center' mb='xs'>
          <Text size='sm' fw={500}>
            Storico Dati ({totalCount})
          </Text>
        </Group>

        <Group mb='xs' align='center' gap='xs'>
          <Filter size={16} color='gray' style={{ verticalAlign: 'middle' }} />
          <SegmentedControl
            value={filter}
            onChange={value =>
              setFilter(value as 'all' | 'heartRate' | 'stateChanges')
            }
            data={[
              { label: 'Tutti', value: 'all' },
              { label: 'Heart Rate', value: 'heartRate' },
              { label: 'Cambi Stato', value: 'stateChanges' },
            ]}
            size='xs'
          />
        </Group>

        {combinedData.length === 0 ? (
          <Text size='xs' c='dimmed' ta='center' py='md'>
            Nessun dato disponibile
          </Text>
        ) : (
          <div style={{ maxHeight: '500px', overflow: 'auto' }}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Orario</Table.Th>
                  <Table.Th>Stato</Table.Th>
                  <Table.Th>HR (bpm)</Table.Th>
                  <Table.Th>HRV (ms)</Table.Th>
                  <Table.Th>LF (ms²)</Table.Th>
                  <Table.Th>HF (ms²)</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {combinedData.map((item, index) => (
                  <Table.Tr key={`${item.type}-${item.timestamp}-${index}`}>
                    <Table.Td>
                      <Text size='xs'>
                        {formatTimestampToTimeWithMs(item.timestamp)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {item.type === 'stateChange' && item.phase ? (
                        <Badge color={getStateColor(item.phase)} size='xs'>
                          {item.phase}
                        </Badge>
                      ) : (
                        <Text size='xs' c='dimmed'>
                          HR
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size='xs' c='dimmed'>
                        {item.hr}{' '}
                        <Text size='10px' c='dimmed' span>
                          (bpm)
                        </Text>
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='xs' c='dimmed'>
                        {item.hrv ? (
                          <>
                            {item.hrv}{' '}
                            <Text size='10px' c='dimmed' span>
                              (ms)
                            </Text>
                          </>
                        ) : (
                          '--'
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='xs' c='dimmed'>
                        {item.lf ? (
                          <>
                            {item.lf}{' '}
                            <Text size='10px' c='dimmed' span>
                              (ms²)
                            </Text>
                          </>
                        ) : (
                          '--'
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='xs' c='dimmed'>
                        {item.hf ? (
                          <>
                            {item.hf}{' '}
                            <Text size='10px' c='dimmed' span>
                              (ms²)
                            </Text>
                          </>
                        ) : (
                          '--'
                        )}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}

        <Card withBorder p='xs' bg='blue.0'>
          <Group gap='xs'>
            <Info size={16} style={{ color: 'blue' }} />
            <Text size='xs' c='blue'>
              I cambi di stato vengono rilevati automaticamente quando il
              battito cardiaco varia oltre le soglie configurate rispetto alla
              baseline.
            </Text>
          </Group>
        </Card>
      </Stack>
    </Card>
  );
};

const AnswersTable = ({ answers = [] }: { answers?: SessionAnswer[] }) => {
  return (
    <Card shadow='sm' withBorder p='md' radius='sm'>
      <Stack gap='xs' mb='md'>
        <Title order={4}>Risposte</Title>
        <Text size='xs' c='dimmed'>
          Risposte fornite durante la sessione
        </Text>
      </Stack>

      <Stack gap='md'>
        <Group justify='space-between' align='center' mb='xs'>
          <Text size='sm' fw={500}>
            Storico Risposte ({answers.length})
          </Text>
        </Group>

        {answers.length === 0 ? (
          <Text size='xs' c='dimmed' ta='center' py='md'>
            Nessuna risposta disponibile
          </Text>
        ) : (
          <div style={{ maxHeight: '500px', overflow: 'auto' }}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Orario</Table.Th>
                  <Table.Th>Domanda</Table.Th>
                  <Table.Th>Risposta</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {answers.map((answer, index) => (
                  <Table.Tr key={`answer-${answer.stepId}-${index}`}>
                    <Table.Td>
                      <Text size='xs'>
                        {(() => {
                          if (!answer.timestamp) {
                            return '--';
                          }
                          const ms = new Date(answer.timestamp).getTime();
                          return Number.isNaN(ms)
                            ? '--'
                            : formatTimestampToTime(ms);
                        })()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='xs'>{answer.question || '--'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='xs'>{answer.answer || '--'}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Stack>
    </Card>
  );
};

const DevicesSection = ({ devices }: DevicesSectionProps) => {
  return (
    <Card shadow='sm' withBorder p='md' radius='sm'>
      <Stack gap='xs' mb='md'>
        <Title order={4}>Dispositivi e sensori</Title>
        <Text size='xs' c='dimmed'>
          Dispositivi utilizzati nella sessione
        </Text>
      </Stack>
      <Stack gap='md'>
        {devices.length > 0 ? (
          devices.map(device => {
            const imageSrc = getDeviceImageSrc(device);
            const deviceCode = device.code || device.clientId || 'N/A';
            const model = device.model || 'N/A';

            return (
              <Card key={device.id} withBorder bg='gray.0' p='xs'>
                <Group gap='sm' align='center'>
                  <Card radius='100%' p='5px' bg='white'>
                    <Image
                      src={imageSrc}
                      h={80}
                      w={80}
                      alt={
                        device.type === 'vr-headset'
                          ? 'VR Headset'
                          : 'Heart Rate Sensor'
                      }
                      style={{
                        objectFit: 'contain',
                      }}
                    />
                  </Card>
                  <Stack gap={4} style={{ flex: 1 }}>
                    <Text fw={500} size='sm'>
                      {device.name || 'Dispositivo'}
                    </Text>
                    <Text size='xs' c='dimmed'>
                      Modello: {model}
                    </Text>
                    <Text size='xs' c='dimmed'>
                      Device Code: {deviceCode}
                    </Text>
                  </Stack>
                </Group>
              </Card>
            );
          })
        ) : (
          <Text c='dimmed' size='sm'>
            Nessun dispositivo disponibile
          </Text>
        )}
      </Stack>
    </Card>
  );
};

const EventCategoriesChart = ({
  categoryCounts,
}: EventCategoriesChartProps) => {
  const chartData = [
    { category: 'P-2', count: categoryCounts['p-2'] },
    { category: 'P-1', count: categoryCounts['p-1'] },
    { category: 'P0', count: categoryCounts['p0'] },
    { category: 'P+1', count: categoryCounts['p+1'] },
    { category: 'P+2', count: categoryCounts['p+2'] },
  ];

  return (
    <Card shadow='sm' withBorder p='md' radius='sm'>
      <Stack gap='xs' mb='md'>
        <Title order={4}>Eventi per Fasi</Title>
        <Text size='xs' c='dimmed'>
          Conteggio episodi nella sessione
        </Text>
      </Stack>
      <div style={{ height: '200px' }}>
        <BarChart
          h={200}
          data={chartData}
          dataKey='category'
          series={[{ name: 'count', color: 'blue' }]}
          withLegend={false}
        />
      </div>
      <Group gap='md' mt='md' wrap='wrap'>
        <Group gap={4}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-purple-6)',
            }}
          />
          <Text size='xs'>P-2</Text>
          <Text size='sm' fw={500}>
            {categoryCounts['p-2']}
          </Text>
        </Group>
        <Group gap={4}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-cyan-6)',
            }}
          />
          <Text size='xs'>P-1</Text>
          <Text size='sm' fw={500}>
            {categoryCounts['p-1']}
          </Text>
        </Group>
        <Group gap={4}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-gray-6)',
            }}
          />
          <Text size='xs'>P0</Text>
          <Text size='sm' fw={500}>
            {categoryCounts['p0']}
          </Text>
        </Group>
        <Group gap={4}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-blue-6)',
            }}
          />
          <Text size='xs'>P+1</Text>
          <Text size='sm' fw={500}>
            {categoryCounts['p+1']}
          </Text>
        </Group>
        <Group gap={4}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-red-6)',
            }}
          />
          <Text size='xs'>P+2</Text>
          <Text size='sm' fw={500}>
            {categoryCounts['p+2']}
          </Text>
        </Group>
      </Group>
    </Card>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export default function CompletedSessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const userId = useAuthUser();
  const userIdStr = userId?.id?.toString();
  const [timeIntervalStart, setTimeIntervalStart] = useState<Date | null>(null);
  const [timeIntervalEnd, setTimeIntervalEnd] = useState<Date | null>(null);

  // Export modal state
  const [
    exportModalOpened,
    { open: openExportModal, close: closeExportModal },
  ] = useDisclosure(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf'>('csv');
  const [exportSelectedSections, setExportSelectedSections] = useState<
    (
      | 'devices'
      | 'patient'
      | 'session'
      | 'events'
      | 'phases'
      | 'answers'
      | 'cibi'
    )[]
  >(['events', 'phases', 'answers']); // Default per CSV

  // Loading state for export
  const [isExporting, setIsExporting] = useState(false);

  // Chart generation for PDF
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Refs for CSV downloads
  const devicesCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const patientCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const sessionCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const eventsCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const phasesCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const answersCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const exposureEventsCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const exposureFoodStatsCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);

  const { data: session, isLoading: configLoading } = useConfigurationQuery(id);

  const { devices } = useUserDevices(userIdStr);

  const sessionDevices = useMemo(() => {
    if (!session) return [];

    const result: Device[] = [];
    const seen = new Set<number>();
    const push = (device: Device) => {
      if (!Number.isFinite(device.id) || seen.has(device.id)) return;
      seen.add(device.id);
      result.push(device);
    };

    const deviceIdRaw = session.device_id;
    const deviceIdNum =
      typeof deviceIdRaw === 'string' ? parseInt(deviceIdRaw, 10) : Number(deviceIdRaw);

    const mainFromRegistry = Number.isFinite(deviceIdNum)
      ? devices.find(device => device.id === deviceIdNum)
      : undefined;

    if (mainFromRegistry) {
      push(mainFromRegistry);
    } else if (Number.isFinite(deviceIdNum) && deviceIdNum > 0) {
      push({
        id: deviceIdNum,
        type: 'vr-headset',
        name: session.deviceName || undefined,
        code: session.deviceCode || undefined,
      });
    }

    const connectedList = session.connectedDevices ?? [];
    const sensorId =
      session.sensor_id != null ? Number(session.sensor_id) : null;

    if (sensorId != null && Number.isFinite(sensorId)) {
      const sensorFromRegistry = devices.find(device => device.id === sensorId);
      if (sensorFromRegistry) {
        push(sensorFromRegistry);
      } else {
        const fromConnected = connectedList.find(d => d.id === sensorId);
        if (fromConnected) {
          push({
            id: fromConnected.id,
            type:
              fromConnected.type === 'vr-headset' ? 'vr-headset' : 'smartwatch',
            name: fromConnected.name || undefined,
            code: fromConnected.code || undefined,
          });
        }
      }
      return result;
    }

    // Fallback: unico sensore riferito da connectedTo (non tutti i connectedDevices live)
    const connectedTo = session.connectedTo?.trim();
    if (connectedTo) {
      const sensorFromRegistry = devices.find(
        device =>
          device.type === 'smartwatch' &&
          (device.code === connectedTo ||
            device.clientId === connectedTo ||
            String(device.id) === connectedTo)
      );
      if (sensorFromRegistry) {
        push(sensorFromRegistry);
      } else {
        const fromConnected = connectedList.find(
          d =>
            d.code === connectedTo ||
            String(d.id) === connectedTo ||
            d.name?.trim() === connectedTo
        );
        if (fromConnected) {
          push({
            id: fromConnected.id,
            type:
              fromConnected.type === 'vr-headset' ? 'vr-headset' : 'smartwatch',
            name: fromConnected.name || undefined,
            code: fromConnected.code || undefined,
          });
        }
      }
    }

    return result;
  }, [session, devices]);

  const hrDataArray = useMemo(() => {
    if (!session) return [];
    return getEffectiveHeartRateData(session);
  }, [session]);

  const metrics = useMemo(() => {
    if (!session) {
      return {
        baseline: 0,
        min: 0,
        max: 0,
        avg: 0,
        hrv: 0,
      };
    }
    return calculateMetrics(hrDataArray, session.baseline || 0);
  }, [hrDataArray, session]);

  const phaseEvents = useMemo(() => {
    if (!session) return [];
    return getPhaseEventsFromSession(session);
  }, [session]);

  const sessionAnswers = useMemo((): SessionAnswer[] => {
    if (!session?.answers?.trim()) {
      return [];
    }

    return parseSessionAnswersJsonToRows(session.answers, session);
  }, [session]);
  const resolvedExposureEvents = useMemo(() => {
    if (!session) return [];
    const fromTop = session.exposureEvents;
    let raw: unknown[] = [];
    if (Array.isArray(fromTop) && fromTop.length > 0) {
      raw = fromTop;
    } else {
      const rawConfig = session.config;
      const configObj =
        typeof rawConfig === 'string'
          ? (() => {
              try {
                return JSON.parse(rawConfig) as Record<string, unknown>;
              } catch {
                return null;
              }
            })()
          : (rawConfig as Record<string, unknown> | undefined);
      const fromConfig = configObj?.[EXPOSURE_EVENTS_CONFIG_KEY];
      raw = Array.isArray(fromConfig) ? fromConfig : [];
    }
    return translateExposureEvents(raw);
  }, [session]);

  const exposureFoodStats = useMemo(() => {
    if (session?.type !== 'exposure' || !resolvedExposureEvents.length)
      return [];
    const hrEvents = hrDataArray.map(e => ({
      timestamp: e.timestamp,
      hr: e.hr ?? 0,
    }));
    return getExposureItemStats(resolvedExposureEvents, hrEvents);
  }, [session?.type, resolvedExposureEvents, hrDataArray]);

  const eventCategoryCounts = useMemo(() => {
    if (!session) {
      return { 'p-2': 0, 'p-1': 0, p0: 0, 'p+1': 0, 'p+2': 0 };
    }
    return countEventsByState(session.results);
  }, [session?.results]);

  const chartData = useMemo(() => {
    const base = pipe(
      hrDataArray,
      map(event => ({
        time: new Date(event.timestamp).toLocaleTimeString('it-IT', {
          hour: '2-digit',
          minute: '2-digit',
        }),
        timestamp: event.timestamp,
        hr: event.hr || 0,
        feedback: [] as FeedbackAtPoint[],
      }))
    );
    if (
      session?.type !== 'exposure' ||
      !resolvedExposureEvents.length ||
      base.length === 0
    ) {
      return base.map(item => omit(item, ['feedback']));
    }
    const feedbacks = resolvedExposureEvents.filter(
      (e): e is Extract<typeof e, { type: 'itemFeedback' }> =>
        e.type === 'itemFeedback'
    );
    for (const fb of feedbacks) {
      const itemIds = Array.isArray(fb.itemIds) ? fb.itemIds : [];
      const firstAfterIndex = base.findIndex(p => p.timestamp >= fb.timestamp);
      if (firstAfterIndex === -1) continue;
      base[firstAfterIndex].feedback!.push({
        itemIds,
        score: fb.score,
        timestamp: fb.timestamp,
      });
    }
    return base;
  }, [hrDataArray, session?.type, resolvedExposureEvents]);

  const handleTimeIntervalChange = useCallback(
    (start: Date | null, end: Date | null) => {
      setTimeIntervalStart(start);
      setTimeIntervalEnd(end);
    },
    []
  );

  const sessionDates = useMemo((): SessionDates => {
    if (!session) {
      return { start: '', end: '', date: '' };
    }
    const startDate = session.created_at
      ? new Date(session.created_at)
      : new Date();
    const endDate = session.updated_at
      ? new Date(session.updated_at)
      : new Date();

    return {
      start: startDate.toLocaleTimeString('it-IT', {
        hour: '2-digit',
        minute: '2-digit',
      }),
      end: endDate.toLocaleTimeString('it-IT', {
        hour: '2-digit',
        minute: '2-digit',
      }),
      date: startDate.toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    };
  }, [session]);

  const duration = useMemo(() => {
    if (!session?.started_at || !session?.ended_at) return '0 s';
    return formatSessionDuration(session.started_at, session.ended_at);
  }, [session]);

  // Prepare CSV data
  const devicesCsvData = useMemo(
    () => prepareDevicesCsvData(sessionDevices),
    [sessionDevices]
  );
  const patientCsvData = useMemo(
    () => (session ? preparePatientCsvData(session) : []),
    [session]
  );
  const sessionCsvData = useMemo(
    () => (session ? prepareSessionCsvData(session, duration) : []),
    [session, duration]
  );
  const eventsCsvData = useMemo(
    () => prepareEventsCsvData(hrDataArray),
    [hrDataArray]
  );
  const phasesCsvData = useMemo(
    () => preparePhasesCsvData(phaseEvents),
    [phaseEvents]
  );
  const answersCsvData = useMemo(
    () =>
      session ? prepareAnswersCsvData(session.answers || null, session) : [],
    [session]
  );

  const exposureFoodDisplay = useMemo((): ExposureFoodDisplayContext => {
    if (!session || session.type !== 'exposure') {
      return { isMultiItem: false, portate: [] };
    }
    const config = getExposureConfigFromSession(session);
    return {
      isMultiItem: Boolean(config.isMultiItem),
      portate: getPortateFromExposureConfig(config),
    };
  }, [session]);

  const exposureEventsCsvData = useMemo(
    () =>
      prepareExposureEventsCsvData(resolvedExposureEvents, exposureFoodDisplay),
    [resolvedExposureEvents, exposureFoodDisplay]
  );

  const isMultiItem = exposureFoodDisplay.isMultiItem;
  const exposureFoodStatsCsvData = useMemo(
    () =>
      prepareExposureItemStatsCsvData(exposureFoodStats, exposureFoodDisplay),
    [exposureFoodStats, exposureFoodDisplay]
  );

  // Prepare chart data for PDF (with feedback for food dots)
  const chartDataForPdf = useMemo(() => {
    if (!hrDataArray || hrDataArray.length === 0) return [];
    const base = hrDataArray.map((event, index) => ({
      time: index + 1,
      timeLabel: new Date(event.timestamp).toLocaleTimeString('it-IT', {
        hour: '2-digit',
        minute: '2-digit',
      }),
      timestamp: event.timestamp,
      hr: event.hr || 0,
      hrv: event.hrv || 0,
      feedback: [] as FeedbackAtPoint[],
    }));
    if (
      session?.type !== 'exposure' ||
      !resolvedExposureEvents.length ||
      base.length === 0
    ) {
      return base.map(item => omit(item, ['feedback']));
    }
    const feedbacks = resolvedExposureEvents.filter(
      (e): e is Extract<typeof e, { type: 'itemFeedback' }> =>
        e.type === 'itemFeedback'
    );
    for (const fb of feedbacks) {
      const itemIds = Array.isArray(fb.itemIds) ? fb.itemIds : [];
      const firstAfterIndex = base.findIndex(p => p.timestamp >= fb.timestamp);
      if (firstAfterIndex === -1) continue;
      base[firstAfterIndex].feedback!.push({
        itemIds,
        score: fb.score,
        timestamp: fb.timestamp,
      });
    }
    return base;
  }, [hrDataArray, session?.type, resolvedExposureEvents]);

  // CSV headers
  const devicesCsvHeaders = [
    { label: 'ID', key: 'ID' },
    { label: 'Nome', key: 'Nome' },
    { label: 'Codice', key: 'Codice' },
    { label: 'Tipo', key: 'Tipo' },
    { label: 'Modello', key: 'Modello' },
    { label: 'Client ID', key: 'Client ID' },
    { label: 'Collegato a', key: 'Collegato a' },
  ];

  const patientCsvHeaders = [
    { label: 'Codice Utente', key: 'Codice Utente' },
    { label: 'ID Utente', key: 'ID Utente' },
    { label: 'ID Sessione', key: 'ID Sessione' },
  ];

  const sessionCsvHeaders = [
    { label: 'ID Sessione', key: 'ID Sessione' },
    { label: 'Tipo App', key: 'Tipo App' },
    { label: 'Scenario/Esperienza', key: 'Scenario/Esperienza' },
    { label: 'Baseline', key: 'Baseline' },
    { label: 'Variazione', key: 'Variazione' },
    { label: 'Durata', key: 'Durata' },
    { label: 'Data Creazione', key: 'Data Creazione' },
    { label: 'Data Aggiornamento', key: 'Data Aggiornamento' },
    { label: 'Stato', key: 'Stato' },
  ];

  const eventsCsvHeaders = [
    { label: 'Timestamp', key: 'Timestamp' },
    { label: 'Data/Ora', key: 'Data/Ora' },
    { label: 'Heart Rate (bpm)', key: 'Heart Rate (bpm)' },
    { label: 'HRV (ms)', key: 'HRV (ms)' },
    { label: 'LF', key: 'LF' },
    { label: 'HF', key: 'HF' },
    { label: 'Device Code', key: 'Device Code' },
  ];

  const phasesCsvHeaders = [
    { label: 'Timestamp', key: 'Timestamp' },
    { label: 'Data/Ora', key: 'Data/Ora' },
    { label: 'Fase', key: 'Fase' },
    { label: 'Heart Rate (bpm)', key: 'Heart Rate (bpm)' },
  ];

  const exposureEventsCsvHeaders = [
    { label: 'Timestamp', key: 'Timestamp' },
    { label: 'Data/Ora', key: 'Data/Ora' },
    { label: 'Tipo', key: 'Tipo' },
    { label: 'Dettaglio', key: 'Dettaglio' },
  ];

  const getExposureItemStatsCsvHeaders = (buffet: boolean) =>
    buffet
      ? [
          { label: 'Gruppo', key: 'Gruppo' },
          {
            label: 'Score ansia (1–5)',
            key: 'Score ansia (1–5)',
          },
          {
            label: 'Tempo visualizzazione cibo (s)',
            key: 'Tempo visualizzazione cibo (s)',
          },
          { label: 'HR al feedback (bpm)', key: 'HR al feedback (bpm)' },
        ]
      : [
          { label: 'Cibo', key: 'Cibo' },
          {
            label: 'Score ansia (1–5)',
            key: 'Score ansia (1–5)',
          },
          {
            label: 'Tempo visualizzazione cibo (s)',
            key: 'Tempo visualizzazione cibo (s)',
          },
          { label: 'HR al feedback (bpm)', key: 'HR al feedback (bpm)' },
        ];

  // Dynamic headers for answers (will be determined at export time)
  const getAnswersCsvHeaders = (data: any[]) => {
    if (data.length === 0) {
      return [{ label: 'Messaggio', key: 'Messaggio' }];
    }

    // Se i dati sono nel formato preparato da prepareAnswersCsvData,
    // usa gli header standard: Orario, Domanda, Risposta, Step ID
    const firstRow = data[0];
    if (firstRow && typeof firstRow === 'object') {
      const keys = Object.keys(firstRow);

      // Se contiene le chiavi standard, usa quelle
      if (
        keys.includes('Orario') ||
        keys.includes('Domanda') ||
        keys.includes('Risposta')
      ) {
        const standardHeaders = [
          { label: 'Orario', key: 'Orario' },
          { label: 'Domanda', key: 'Domanda' },
          { label: 'Risposta', key: 'Risposta' },
        ];

        // Aggiungi Step ID solo se presente
        if (keys.includes('Step ID')) {
          standardHeaders.push({ label: 'Step ID', key: 'Step ID' });
        }

        return standardHeaders;
      }

      // Altrimenti usa le chiavi dinamiche
      const dynamicHeaders = keys.map(key => ({ label: key, key }));
      return dynamicHeaders;
    }

    return [{ label: 'Messaggio', key: 'Messaggio' }];
  };

  // Generate chart image for PDF
  const generateChartImage = useCallback(async () => {
    if (
      !chartContainerRef.current ||
      chartDataForPdf.length === 0 ||
      !session
    ) {
      return null;
    }

    try {
      // Make container temporarily visible for html2canvas
      const container = chartContainerRef.current;
      const originalStyle = container.style.cssText;
      container.style.cssText = `
        position: fixed;
        left: 0;
        top: 0;
        width: 800px;
        height: 300px;
        backgroundColor: #ffffff;
        z-index: -1;
        opacity: 0;
        pointer-events: none;
      `;

      // Wait for Recharts to fully render
      await new Promise(resolve => setTimeout(resolve, 2000));

      const canvas = await html2canvas(container, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: true, // Enable logging for debugging
        useCORS: true,
        allowTaint: true,
        width: 800,
        height: 300,
        onclone: clonedDoc => {
          // Ensure the cloned document has the chart visible
          const clonedContainer = clonedDoc.querySelector(
            '[data-chart-container]'
          ) as HTMLElement;
          if (clonedContainer) {
            clonedContainer.style.cssText = `
              position: fixed;
              left: 0;
              top: 0;
              width: 500px;
              height: 300px;
              background-color: #ffffff;
              z-index: 9999;
              opacity: 1;
            `;
          }
        },
      });

      // Restore original style
      container.style.cssText = originalStyle;

      const base64 = canvas.toDataURL('image/png');
      return base64;
    } catch {
      return null;
    }
  }, [chartDataForPdf, session]);

  // PDF Styles
  const pdfStyles = useMemo(
    () =>
      StyleSheet.create({
        page: {
          padding: 30,
          fontSize: 10,
          fontFamily: 'Helvetica',
        },
        title: {
          fontSize: 20,
          marginBottom: 10,
          fontWeight: 'bold',
        },
        subtitle: {
          fontSize: 12,
          marginBottom: 20,
          color: '#666',
        },
        section: {
          marginBottom: 20,
        },
        sectionTitle: {
          fontSize: 14,
          fontWeight: 'bold',
          marginBottom: 10,
          marginTop: 15,
          borderBottom: '1px solid #ccc',
          paddingBottom: 5,
        },
        row: {
          flexDirection: 'row',
          marginBottom: 5,
        },
        label: {
          width: '40%',
          fontWeight: 'bold',
        },
        value: {
          width: '60%',
        },
        table: {
          marginTop: 10,
        },
        tableRow: {
          flexDirection: 'row',
          borderBottom: '1px solid #eee',
          paddingVertical: 5,
        },
        tableHeader: {
          backgroundColor: '#f0f0f0',
          fontWeight: 'bold',
        },
        tableCell: {
          flex: 1,
          padding: 5,
        },
      }),
    []
  );

  // Generate PDF Document - Pure function to avoid Mantine context issues
  const generatePdfDocument = useCallback(
    (chartImage: string | null = null) => {
      if (!session || !pdfStyles) return null;

      const pdfPageHeader = (
        <View style={{ marginBottom: 15 }}>
          <PdfText style={pdfStyles.title}>
            Report Sessione #{session.id}
          </PdfText>
          <PdfText style={pdfStyles.subtitle}>
            Data esportazione:{' '}
            {new Date().toLocaleString('it-IT', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </PdfText>
        </View>
      );

      // Create a pure PDF document without any Mantine dependencies
      const PdfDocument = (
        <Document>
          {/* Info Utente */}
          {exportSelectedSections.includes('patient') && (
            <Page size='A4' style={pdfStyles.page}>
              {pdfPageHeader}
              <View style={pdfStyles.section}>
                <PdfText style={pdfStyles.sectionTitle}>Info Utente</PdfText>
                <View style={pdfStyles.row}>
                  <PdfText style={pdfStyles.label}>Codice Utente:</PdfText>
                  <PdfText style={pdfStyles.value}>
                    {session.patientCode || 'N/A'}
                  </PdfText>
                </View>
                <View style={pdfStyles.row}>
                  <PdfText style={pdfStyles.label}>ID Utente:</PdfText>
                  <PdfText style={pdfStyles.value}>
                    {session.patient_id || 'N/A'}
                  </PdfText>
                </View>
              </View>
            </Page>
          )}

          {/* Info Sessione */}
          {exportSelectedSections.includes('session') && (
            <Page size='A4' style={pdfStyles.page}>
              {pdfPageHeader}
              <View style={pdfStyles.section}>
                <PdfText style={pdfStyles.sectionTitle}>Info Sessione</PdfText>
                <View style={pdfStyles.row}>
                  <PdfText style={pdfStyles.label}>Tipo App:</PdfText>
                  <PdfText style={pdfStyles.value}>
                    {getSessionTypeDisplay(session.type, session.scenario)
                      .productLabel ||
                      session.type ||
                      'N/A'}
                  </PdfText>
                </View>
                <View style={pdfStyles.row}>
                  <PdfText style={pdfStyles.label}>
                    Scenario/Esperienza:
                  </PdfText>
                  <PdfText style={pdfStyles.value}>
                    {getScenarioDisplayLabel(
                      session.type,
                      session.scenario,
                      session.video?.name
                    )}
                  </PdfText>
                </View>
                <View style={pdfStyles.row}>
                  <PdfText style={pdfStyles.label}>Baseline:</PdfText>
                  <PdfText style={pdfStyles.value}>
                    {session.baseline && session.baseline > 0
                      ? `${session.baseline} bpm`
                      : 'assente'}
                  </PdfText>
                </View>
                {getAppConfig(session?.type ?? 'default').hasVariation && (
                  <View style={pdfStyles.row}>
                    <PdfText style={pdfStyles.label}>Variazione:</PdfText>
                    <PdfText style={pdfStyles.value}>
                      {session.config?.variation
                        ? `${session.config.variation}%`
                        : 'N/A'}
                    </PdfText>
                  </View>
                )}
                <View style={pdfStyles.row}>
                  <PdfText style={pdfStyles.label}>Durata sessione:</PdfText>
                  <PdfText style={pdfStyles.value}>{duration}</PdfText>
                </View>
                <View style={pdfStyles.row}>
                  <PdfText style={pdfStyles.label}>Inizio:</PdfText>
                  <PdfText style={pdfStyles.value}>
                    {session.started_at
                      ? new Date(session.started_at).toLocaleString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'N/A'}
                  </PdfText>
                </View>
                <View style={pdfStyles.row}>
                  <PdfText style={pdfStyles.label}>Fine:</PdfText>
                  <PdfText style={pdfStyles.value}>
                    {session.ended_at
                      ? new Date(session.ended_at).toLocaleString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'N/A'}
                  </PdfText>
                </View>
                {session.video?.duration && (
                  <View style={pdfStyles.row}>
                    <PdfText style={pdfStyles.label}>Durata del video:</PdfText>
                    <PdfText style={pdfStyles.value}>
                      {Math.round(session.video.duration / 60)} min
                    </PdfText>
                  </View>
                )}
                <View style={pdfStyles.row}>
                  <PdfText style={pdfStyles.label}>Esperienza:</PdfText>
                  <PdfText style={pdfStyles.value}>
                    {session.scenario === 'esperienza-vr'
                      ? 'esperienza-vr'
                      : session.scenario || 'N/A'}
                  </PdfText>
                </View>
                <View style={pdfStyles.row}>
                  <PdfText style={pdfStyles.label}>Link sessione:</PdfText>
                  <Link
                    src={`https://platform.become-hub.com/sessions/completed/${session.id}`}
                    style={pdfStyles.value}
                  >
                    https://platform.become-hub.com/sessions/completed/
                    {session.id}
                  </Link>
                </View>
              </View>
            </Page>
          )}

          {/* Dispositivi */}
          {exportSelectedSections.includes('devices') &&
            sessionDevices.length > 0 && (
              <Page size='A4' style={pdfStyles.page}>
                {pdfPageHeader}
                <View style={pdfStyles.section}>
                  <PdfText style={pdfStyles.sectionTitle}>Dispositivi</PdfText>
                  <View style={pdfStyles.table}>
                    <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
                      <PdfText style={pdfStyles.tableCell}>ID</PdfText>
                      <PdfText style={pdfStyles.tableCell}>Nome</PdfText>
                      <PdfText style={pdfStyles.tableCell}>Device Code</PdfText>
                      <PdfText style={pdfStyles.tableCell}>Modello</PdfText>
                    </View>
                    {sessionDevices.map(device => (
                      <View key={device.id} style={pdfStyles.tableRow}>
                        <PdfText style={pdfStyles.tableCell}>
                          {device.id || 'N/A'}
                        </PdfText>
                        <PdfText style={pdfStyles.tableCell}>
                          {device.name || 'N/A'}
                        </PdfText>
                        <PdfText style={pdfStyles.tableCell}>
                          {device.code || device.clientId || 'N/A'}
                        </PdfText>
                        <PdfText style={pdfStyles.tableCell}>
                          {device.model || 'N/A'}
                        </PdfText>
                      </View>
                    ))}
                  </View>
                </View>
              </Page>
            )}

          {/* Eventi */}
          {exportSelectedSections.includes('events') &&
            eventsCsvData.length > 0 && (
              <Page size='A4' style={pdfStyles.page}>
                {pdfPageHeader}
                <View style={pdfStyles.section}>
                  <PdfText style={pdfStyles.sectionTitle}>
                    Eventi ({eventsCsvData.length})
                  </PdfText>
                  {chartImage && (
                    <View style={{ marginBottom: 15 }}>
                      <PdfText
                        style={{
                          fontSize: 12,
                          fontWeight: 'bold',
                          marginBottom: 8,
                        }}
                      >
                        Grafico Heart Rate e HRV
                      </PdfText>
                      <PdfImage
                        src={chartImage}
                        style={{ width: '100%', maxWidth: 800, height: 300 }}
                      />
                      <View
                        style={{
                          flexDirection: 'row',
                          marginTop: 12,
                          gap: 12,
                        }}
                      >
                        <View
                          style={{
                            flex: 1,
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            padding: 8,
                          }}
                        >
                          <PdfText
                            style={{
                              fontSize: 9,
                              color: '#666',
                              marginBottom: 4,
                            }}
                          >
                            Baseline HR
                          </PdfText>
                          <PdfText
                            style={{
                              fontSize: 14,
                              fontWeight: 'bold',
                            }}
                          >
                            {metrics.baseline} bpm
                          </PdfText>
                        </View>
                        <View
                          style={{
                            flex: 1,
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            padding: 8,
                          }}
                        >
                          <PdfText
                            style={{
                              fontSize: 9,
                              color: '#666',
                              marginBottom: 4,
                            }}
                          >
                            Minima
                          </PdfText>
                          <PdfText
                            style={{
                              fontSize: 14,
                              fontWeight: 'bold',
                            }}
                          >
                            {metrics.min} bpm
                          </PdfText>
                        </View>
                        <View
                          style={{
                            flex: 1,
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            padding: 8,
                          }}
                        >
                          <PdfText
                            style={{
                              fontSize: 9,
                              color: '#666',
                              marginBottom: 4,
                            }}
                          >
                            Massima
                          </PdfText>
                          <PdfText
                            style={{
                              fontSize: 14,
                              fontWeight: 'bold',
                            }}
                          >
                            {metrics.max} bpm
                          </PdfText>
                        </View>
                        <View
                          style={{
                            flex: 1,
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            padding: 8,
                          }}
                        >
                          <PdfText
                            style={{
                              fontSize: 9,
                              color: '#666',
                              marginBottom: 4,
                            }}
                          >
                            Media sessione
                          </PdfText>
                          <PdfText
                            style={{
                              fontSize: 14,
                              fontWeight: 'bold',
                            }}
                          >
                            {metrics.avg} bpm
                          </PdfText>
                          <PdfText
                            style={{ fontSize: 9, color: '#666', marginTop: 2 }}
                          >
                            HRV {metrics.hrv} ms
                          </PdfText>
                        </View>
                      </View>
                    </View>
                  )}
                  <PdfText
                    style={{
                      fontSize: 9,
                      color: '#666',
                      marginBottom: 10,
                      fontStyle: 'italic',
                    }}
                  >
                    Questi eventi riguardano solo il periodo di tracciamento,
                    escludono il calcolo della baseline.
                  </PdfText>
                  <View style={pdfStyles.table}>
                    <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
                      <PdfText style={[pdfStyles.tableCell, { flex: 2 }]}>
                        Data/Ora
                      </PdfText>
                      <PdfText style={pdfStyles.tableCell}>HR (bpm)</PdfText>
                      <PdfText style={pdfStyles.tableCell}>HRV (ms)</PdfText>
                    </View>
                    {eventsCsvData.slice(0, 50).map((event, idx) => (
                      <View key={idx} style={pdfStyles.tableRow}>
                        <PdfText style={[pdfStyles.tableCell, { flex: 2 }]}>
                          {String(event['Data/Ora'] || 'N/A')}
                        </PdfText>
                        <PdfText style={pdfStyles.tableCell}>
                          {String(event['Heart Rate (bpm)'] ?? 'N/A')}
                        </PdfText>
                        <PdfText style={pdfStyles.tableCell}>
                          {String(event['HRV (ms)'] ?? 'N/A')}
                        </PdfText>
                      </View>
                    ))}
                    {eventsCsvData.length > 50 && (
                      <PdfText style={{ marginTop: 10, fontSize: 8 }}>
                        ... e altri {eventsCsvData.length - 50} eventi
                      </PdfText>
                    )}
                  </View>
                </View>
              </Page>
            )}

          {/* Cambi Fasi */}
          {exportSelectedSections.includes('phases') &&
            phasesCsvData.length > 0 && (
              <Page size='A4' style={pdfStyles.page}>
                {pdfPageHeader}
                <View style={pdfStyles.section}>
                  <PdfText style={pdfStyles.sectionTitle}>
                    Cambi Fasi ({phasesCsvData.length})
                  </PdfText>
                  <View style={pdfStyles.table}>
                    <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
                      <PdfText style={[pdfStyles.tableCell, { flex: 2 }]}>
                        Data/Ora
                      </PdfText>
                      <PdfText style={pdfStyles.tableCell}>Fase</PdfText>
                      <PdfText style={pdfStyles.tableCell}>HR (bpm)</PdfText>
                    </View>
                    {phasesCsvData.map((phase, idx) => (
                      <View key={idx} style={pdfStyles.tableRow}>
                        <PdfText style={[pdfStyles.tableCell, { flex: 2 }]}>
                          {String(phase['Data/Ora'] || 'N/A')}
                        </PdfText>
                        <PdfText style={pdfStyles.tableCell}>
                          {String(phase.Fase || 'N/A')}
                        </PdfText>
                        <PdfText style={pdfStyles.tableCell}>
                          {String(phase['Heart Rate (bpm)'] ?? 'N/A')}
                        </PdfText>
                      </View>
                    ))}
                  </View>
                </View>
              </Page>
            )}

          {/* Info cibi (solo exposure) */}
          {exportSelectedSections.includes('cibi') &&
            session?.type === 'exposure' && (
              <Page size='A4' style={pdfStyles.page}>
                {pdfPageHeader}
                <View style={pdfStyles.section}>
                  <PdfText style={pdfStyles.sectionTitle}>Info cibi</PdfText>

                  {resolvedExposureEvents.length > 0 && (
                    <>
                      <PdfText
                        style={{
                          fontSize: 12,
                          fontWeight: 'bold',
                          marginBottom: 8,
                          marginTop: 10,
                        }}
                      >
                        Eventi cibi e tutorial ({resolvedExposureEvents.length})
                      </PdfText>
                      <View style={pdfStyles.table}>
                        <View
                          style={[pdfStyles.tableRow, pdfStyles.tableHeader]}
                        >
                          <PdfText style={[pdfStyles.tableCell, { flex: 1 }]}>
                            Tipo
                          </PdfText>
                          <PdfText style={[pdfStyles.tableCell, { flex: 1.5 }]}>
                            Data/Ora
                          </PdfText>
                          <PdfText style={[pdfStyles.tableCell, { flex: 2 }]}>
                            Dettaglio
                          </PdfText>
                        </View>
                        {exposureEventsCsvData.map((row, idx) => (
                          <View key={idx} style={pdfStyles.tableRow}>
                            <PdfText style={[pdfStyles.tableCell, { flex: 1 }]}>
                              {String(row.Tipo ?? 'N/A')}
                            </PdfText>
                            <PdfText
                              style={[pdfStyles.tableCell, { flex: 1.5 }]}
                            >
                              {String(row['Data/Ora'] ?? 'N/A')}
                            </PdfText>
                            <PdfText style={[pdfStyles.tableCell, { flex: 2 }]}>
                              {String(row.Dettaglio ?? '—')}
                            </PdfText>
                          </View>
                        ))}
                      </View>
                    </>
                  )}

                  {exposureFoodStats.length > 0 && (
                    <>
                      <PdfText
                        style={{
                          fontSize: 12,
                          fontWeight: 'bold',
                          marginBottom: 4,
                          marginTop: 15,
                        }}
                      >
                        Statistiche per {isMultiItem ? 'gruppo' : 'elemento'} (
                        {exposureFoodStats.length})
                      </PdfText>
                      <PdfText
                        style={{
                          fontSize: 9,
                          color: '#666',
                          marginBottom: 8,
                        }}
                      >
                        Score ansia: 1 = nessuna ansia, 5 = ansia massima.
                        Tempo: durata visualizzazione del cibo.
                      </PdfText>
                      <View style={pdfStyles.table}>
                        <View
                          style={[pdfStyles.tableRow, pdfStyles.tableHeader]}
                        >
                          <PdfText style={[pdfStyles.tableCell, { flex: 1.5 }]}>
                            {isMultiItem ? 'Gruppo' : 'Elemento'}
                          </PdfText>
                          <PdfText style={pdfStyles.tableCell}>
                            Score ansia
                          </PdfText>
                          <PdfText style={pdfStyles.tableCell}>
                            Tempo vis. cibo (s)
                          </PdfText>
                          <PdfText style={pdfStyles.tableCell}>
                            HR feedback
                          </PdfText>
                        </View>
                        {exposureFoodStats.map((stat, idx) => (
                          <View key={idx} style={pdfStyles.tableRow}>
                            <PdfText
                              style={[pdfStyles.tableCell, { flex: 1.5 }]}
                            >
                              {formatExposureItemStatLabel({
                                itemIds: stat.itemIds,
                                portataNumber: findPortataNumber(
                                  exposureFoodDisplay.portate,
                                  stat.itemIds
                                ),
                                isMultiItem,
                              })}
                            </PdfText>
                            <PdfText style={pdfStyles.tableCell}>
                              {stat.score}
                            </PdfText>
                            <PdfText style={pdfStyles.tableCell}>
                              {(stat.viewTimeMs / 1000).toFixed(1)}
                            </PdfText>
                            <PdfText style={pdfStyles.tableCell}>
                              {stat.hrAtFeedback ?? '—'}
                            </PdfText>
                          </View>
                        ))}
                      </View>
                    </>
                  )}

                  {resolvedExposureEvents.length === 0 &&
                    exposureFoodStats.length === 0 && (
                      <PdfText>Nessun evento elementi/tutorial</PdfText>
                    )}
                </View>
              </Page>
            )}

          {/* Risposte */}
          {exportSelectedSections.includes('answers') && (
            <Page size='A4' style={pdfStyles.page}>
              {pdfPageHeader}
              <View style={pdfStyles.section}>
                <PdfText style={pdfStyles.sectionTitle}>Risposte</PdfText>
                {answersCsvData.length > 0 &&
                answersCsvData[0] &&
                typeof answersCsvData[0] === 'object' &&
                'Messaggio' in answersCsvData[0] &&
                (answersCsvData[0] as Record<string, unknown>).Messaggio ===
                  'Nessuna risposta disponibile' ? (
                  <PdfText>Nessuna risposta disponibile</PdfText>
                ) : (
                  <View style={pdfStyles.table}>
                    {answersCsvData.map((answer, idx) => (
                      <View key={idx} style={{ marginBottom: 10 }}>
                        {Object.entries(answer).map(([key, value]) => (
                          <View key={key} style={pdfStyles.row}>
                            <PdfText style={pdfStyles.label}>{key}:</PdfText>
                            <PdfText style={pdfStyles.value}>
                              {String(value || 'N/A')}
                            </PdfText>
                          </View>
                        ))}
                      </View>
                    ))}
                  </View>
                )}
              </View>
            </Page>
          )}
        </Document>
      );

      return PdfDocument;
    },
    [
      session,
      exportSelectedSections,
      sessionDevices,
      eventsCsvData,
      phasesCsvData,
      answersCsvData,
      exposureEventsCsvData,
      exposureFoodStats,
      exposureFoodDisplay,
      isMultiItem,
      resolvedExposureEvents,
      duration,
      pdfStyles,
      metrics,
    ]
  );

  const handleExportCsv = useCallback(() => {
    // Per CSV, filtrare solo eventi, fasi, answers, cibi
    const csvAllowedSections = exportSelectedSections.filter(s =>
      ['events', 'phases', 'answers', 'cibi'].includes(s)
    );

    if (csvAllowedSections.length === 0) {
      notifications.show({
        color: 'yellow',
        title: 'Attenzione',
        message:
          'Per CSV puoi esportare solo Eventi, Cambi Fasi, Risposte e/o Info cibi',
      });
      return;
    }

    setIsExporting(true);

    // Trigger downloads with a small delay between each
    const refs = [
      { ref: eventsCsvRef, section: 'events' },
      { ref: phasesCsvRef, section: 'phases' },
      { ref: answersCsvRef, section: 'answers' },
      { ref: exposureEventsCsvRef, section: 'cibi' },
      { ref: exposureFoodStatsCsvRef, section: 'cibi' },
    ];

    let delay = 0;
    refs.forEach(({ ref, section }) => {
      if (csvAllowedSections.includes(section as any)) {
        setTimeout(() => {
          if (ref.current) {
            ref.current.link?.click();
          }
        }, delay);
        delay += 200; // 200ms delay between each download
      }
    });

    // Impostare false dopo un breve delay per permettere ai download di iniziare
    setTimeout(() => {
      setIsExporting(false);
      closeExportModal();
      notifications.show({
        color: 'green',
        title: 'Esportazione completata',
        message: `Esportate ${csvAllowedSections.length} sezione${
          csvAllowedSections.length > 1 ? 'i' : ''
        }`,
      });
    }, 500);
  }, [exportSelectedSections, closeExportModal]);

  const handleExportPdf = useCallback(async () => {
    if (exportSelectedSections.length === 0) {
      notifications.show({
        color: 'yellow',
        title: 'Attenzione',
        message: 'Seleziona almeno una sezione da esportare',
      });
      return;
    }

    setIsExporting(true);
    try {
      if (!session) {
        notifications.show({
          color: 'red',
          title: 'Errore',
          message: 'Sessione non disponibile',
        });
        return;
      }

      // Generate chart image if events are selected
      let chartImage: string | null = null;
      if (
        exportSelectedSections.includes('events') &&
        chartDataForPdf.length > 0
      ) {
        chartImage = await generateChartImage();
      }

      // Generate PDF in a separate context to avoid Mantine conflicts
      const doc = generatePdfDocument(chartImage);
      if (!doc) {
        notifications.show({
          color: 'red',
          title: 'Errore',
          message: 'Impossibile generare il documento PDF',
        });
        return;
      }

      // Generate blob using pdf().toBlob()
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sessione_${session?.id}_report_${
        new Date().toISOString().split('T')[0]
      }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      closeExportModal();
      notifications.show({
        color: 'green',
        title: 'Esportazione completata',
        message: 'PDF generato con successo',
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      notifications.show({
        color: 'red',
        title: 'Errore',
        message: 'Errore durante la generazione del PDF',
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    exportSelectedSections,
    generatePdfDocument,
    session,
    closeExportModal,
    chartDataForPdf,
    generateChartImage,
  ]);

  if (configLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 85px)',
        }}
      >
        <Loader size='lg' />
      </div>
    );
  }

  if (!session) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 85px)',
        }}
      >
        <Text c='red'>Sessione non trovata</Text>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        minHeight: 'calc(100vh - 85px)',
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <Group justify='space-between' align='center' mb='md'>
        <div>
          <Group gap='xs' mb='xs'>
            <Button
              variant='subtle'
              size='sm'
              onClick={() => navigate('/sessions/completed')}
            >
              {session.type === 'body_image'
                ? '← Sessioni completate'
                : '← Sessioni / Heart rate'}
            </Button>
          </Group>
          <Title order={2}>Dettaglio sessione {id}</Title>
        </div>
        <Group gap='md'>
          <Group gap='xs'>
            <Calendar size={18} />
            <Text size='sm'>
              Data di creazione: {sessionDates.date} · {sessionDates.start} -{' '}
              {sessionDates.end}
            </Text>
          </Group>
          <Badge color='green' size='lg' variant='light'>
            Completata
          </Badge>
          {session.type !== 'body_image' && (
            <Button
              variant='outline'
              size='sm'
              onClick={openExportModal}
              leftSection={<Download size={16} />}
            >
              Esporta
            </Button>
          )}
        </Group>
      </Group>

      <Divider mb='lg' />

      {session.type === 'body_image' ? (
        <BodyImageCompletedSessionView session={session} />
      ) : (
        <Grid gutter='lg'>
          {/* Left Column */}
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Stack gap='lg'>
              <PatientSection
                session={session}
                patientCode={session.patientCode || 'N/A'}
              />
              <SessionSection session={session} duration={duration} />
              {chartData.length > 0 && (
                <HeartRateChartSection
                  chartData={chartData}
                  metrics={metrics}
                  baseline={session.baseline || 0}
                  onTimeIntervalChange={handleTimeIntervalChange}
                  timeIntervalStart={timeIntervalStart}
                  timeIntervalEnd={timeIntervalEnd}
                  tachycardiaEpisodes={
                    eventCategoryCounts['p+1'] + eventCategoryCounts['p+2']
                  }
                />
              )}
              <Tabs defaultValue='events'>
                <Tabs.List>
                  <Tabs.Tab value='events'>Eventi</Tabs.Tab>
                  {session?.type === 'exposure' && (
                    <Tabs.Tab value='cibi'>Cibi</Tabs.Tab>
                  )}
                  <Tabs.Tab value='answers'>Risposte</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value='events' pt='md'>
                  <EventsTable events={hrDataArray} phaseEvents={phaseEvents} />
                </Tabs.Panel>

                {session?.type === 'exposure' && (
                  <Tabs.Panel value='cibi' pt='md'>
                    <Card shadow='sm' withBorder p='md' radius='sm'>
                      <Stack gap='xs' mb='md'>
                        <Title order={4}>Cibi</Title>
                        <Text size='xs' c='dimmed'>
                          Eventi exposure e tutorial durante la sessione
                        </Text>
                      </Stack>
                      <Stack gap='md'>
                        {resolvedExposureEvents.length ? (
                          <>
                            <Text size='sm' fw={500}>
                              Storico eventi ({resolvedExposureEvents.length})
                            </Text>
                            <div
                              style={{ maxHeight: '500px', overflow: 'auto' }}
                            >
                              <Table withTableBorder withColumnBorders>
                                <Table.Thead>
                                  <Table.Tr>
                                    <Table.Th>Tipo</Table.Th>
                                    <Table.Th>Timestamp</Table.Th>
                                    <Table.Th>Dettaglio</Table.Th>
                                  </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                  {[...resolvedExposureEvents]
                                    .sort((a, b) => a.timestamp - b.timestamp)
                                    .map((event, index) => (
                                      <Table.Tr key={index}>
                                        <Table.Td>
                                          {event.type === 'tutorialCompleted'
                                            ? 'Tutorial completato'
                                            : event.type === 'itemShown'
                                              ? getExposureItemEventTypeLabel(
                                                  'itemShown',
                                                  isMultiItem
                                                )
                                              : event.type === 'itemFeedback'
                                                ? getExposureItemEventTypeLabel(
                                                    'itemFeedback',
                                                    isMultiItem
                                                  )
                                                : event.type ===
                                                    'sessionStartedAt'
                                                  ? 'Inizio sessione'
                                                  : event.type ===
                                                      'sessionEndedAt'
                                                    ? 'Fine sessione'
                                                    : event.type === 'mealEnded'
                                                      ? 'Pasto terminato'
                                                      : '—'}
                                        </Table.Td>
                                        <Table.Td>
                                          {formatTimestampToTime(
                                            event.timestamp
                                          )}
                                        </Table.Td>
                                        <Table.Td>
                                          {(event.type === 'itemShown' ||
                                            event.type === 'itemFeedback') &&
                                            formatExposureItemEventDetail({
                                              eventType: event.type,
                                              itemIds: Array.isArray(
                                                event.itemIds
                                              )
                                                ? event.itemIds
                                                : [],
                                              portataNumber: isMultiItem
                                                ? findPortataNumber(
                                                    exposureFoodDisplay.portate,
                                                    Array.isArray(event.itemIds)
                                                      ? event.itemIds
                                                      : []
                                                  )
                                                : null,
                                              isMultiItem,
                                              score:
                                                event.type === 'itemFeedback'
                                                  ? event.score
                                                  : undefined,
                                            })}
                                          {event.type === 'mealEnded' &&
                                            (event.clientId ?? '—')}
                                          {event.type !== 'itemShown' &&
                                            event.type !== 'itemFeedback' &&
                                            event.type !== 'mealEnded' &&
                                            '—'}
                                        </Table.Td>
                                      </Table.Tr>
                                    ))}
                                </Table.Tbody>
                              </Table>
                            </div>
                          </>
                        ) : (
                          <Text size='xs' c='dimmed' ta='center' py='md'>
                            Nessun evento elementi/tutorial
                          </Text>
                        )}
                      </Stack>
                    </Card>
                  </Tabs.Panel>
                )}

                {sessionAnswers.length > 0 && (
                  <Tabs.Panel value='answers' pt='md'>
                    <AnswersTable answers={sessionAnswers} />
                  </Tabs.Panel>
                )}
              </Tabs>
            </Stack>
          </Grid.Col>

          {/* Right Column */}
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Stack gap='lg'>
              {sessionDevices.length > 0 && (
                <DevicesSection devices={sessionDevices} />
              )}
              {getAppConfig(session?.type ?? 'default').hasVariation && (
                <EventCategoriesChart categoryCounts={eventCategoryCounts} />
              )}
              {session?.type === 'exposure' && exposureFoodStats.length > 0 && (
                <Card shadow='sm' withBorder p='md' radius='sm'>
                  <Stack gap='xs' mb='md'>
                    <Title order={4}>Statistiche cibi (Exposure)</Title>
                    <Text size='xs' c='dimmed'>
                      Score ansia: 1 = nessuna ansia, 5 = ansia massima. Tempo:
                      durata visualizzazione del cibo da parte dell&apos;utente.
                    </Text>
                  </Stack>
                  <Stack gap='sm'>
                    {exposureFoodStats.map((stat, index) => (
                      <Paper key={index} p='sm' withBorder radius='sm'>
                        <Group justify='space-between' wrap='nowrap'>
                          <Text size='sm' fw={500}>
                            {formatExposureItemStatLabel({
                              itemIds: stat.itemIds,
                              portataNumber: findPortataNumber(
                                exposureFoodDisplay.portate,
                                stat.itemIds
                              ),
                              isMultiItem,
                            })}
                          </Text>
                          <Text size='sm' c='dimmed'>
                            Score: {stat.score}
                          </Text>
                        </Group>
                        <Group gap='lg' mt='xs'>
                          <Text size='xs'>
                            Tempo visualizzazione cibo:{' '}
                            {(stat.viewTimeMs / 1000).toFixed(1)} s
                          </Text>
                          <Text size='xs'>
                            HR al feedback:{' '}
                            {stat.hrAtFeedback != null
                              ? `${stat.hrAtFeedback} bpm`
                              : '—'}
                          </Text>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </Card>
              )}
              {/* <DoctorNotesSection
              notes=''
              lastUpdated={`${sessionDates.date} · ${sessionDates.end}`}
              technicianName='[Nome Tecnico]'
            /> */}
            </Stack>
          </Grid.Col>
        </Grid>
      )}

      {/* Export Modal */}
      {session.type !== 'body_image' && (
        <Modal
          opened={exportModalOpened}
          onClose={closeExportModal}
          title='Esporta Dati Sessione'
          centered
          size='md'
        >
          <Stack gap='md'>
            <div>
              <Text size='sm' fw={500} mb='xs'>
                Formato esportazione
              </Text>
              <SegmentedControl
                value={exportFormat}
                onChange={(value: string) => {
                  const format = value as 'csv' | 'pdf';
                  setExportFormat(format);
                  // Reset selezioni quando si cambia formato
                  if (format === 'csv') {
                    setExportSelectedSections(['events', 'phases', 'answers']);
                  } else {
                    setExportSelectedSections([
                      'devices',
                      'patient',
                      'session',
                      'events',
                      'phases',
                      'answers',
                    ]);
                  }
                }}
                data={[
                  { label: 'CSV', value: 'csv' },
                  { label: 'PDF', value: 'pdf' },
                ]}
                fullWidth
              />
            </div>

            <Text size='sm' c='dimmed'>
              {exportFormat === 'csv'
                ? 'Seleziona le sezioni da esportare. Per CSV puoi esportare solo Eventi, Cambi Fasi e/o Risposte. Ogni sezione verrà esportata in un file separato.'
                : 'Seleziona le sezioni da esportare. Tutte le sezioni selezionate verranno incluse in un unico PDF.'}
            </Text>

            {exportFormat === 'pdf' && (
              <>
                <Checkbox
                  label='Dispositivi'
                  checked={exportSelectedSections.includes('devices')}
                  onChange={e => {
                    if (e.target.checked) {
                      setExportSelectedSections(prev => [...prev, 'devices']);
                    } else {
                      setExportSelectedSections(prev =>
                        prev.filter(s => s !== 'devices')
                      );
                    }
                  }}
                />

                <Checkbox
                  label='Info Utente'
                  checked={exportSelectedSections.includes('patient')}
                  onChange={e => {
                    if (e.target.checked) {
                      setExportSelectedSections(prev => [...prev, 'patient']);
                    } else {
                      setExportSelectedSections(prev =>
                        prev.filter(s => s !== 'patient')
                      );
                    }
                  }}
                />

                <Checkbox
                  label='Info Sessione'
                  checked={exportSelectedSections.includes('session')}
                  onChange={e => {
                    if (e.target.checked) {
                      setExportSelectedSections(prev => [...prev, 'session']);
                    } else {
                      setExportSelectedSections(prev =>
                        prev.filter(s => s !== 'session')
                      );
                    }
                  }}
                />
              </>
            )}

            <Checkbox
              label='Eventi'
              checked={exportSelectedSections.includes('events')}
              onChange={e => {
                if (e.target.checked) {
                  setExportSelectedSections(prev => [...prev, 'events']);
                } else {
                  setExportSelectedSections(prev =>
                    prev.filter(s => s !== 'events')
                  );
                }
              }}
            />

            <Checkbox
              label='Cambi Fasi'
              checked={exportSelectedSections.includes('phases')}
              onChange={e => {
                if (e.target.checked) {
                  setExportSelectedSections(prev => [...prev, 'phases']);
                } else {
                  setExportSelectedSections(prev =>
                    prev.filter(s => s !== 'phases')
                  );
                }
              }}
            />

            <Checkbox
              label='Risposte'
              checked={exportSelectedSections.includes('answers')}
              onChange={e => {
                if (e.target.checked) {
                  setExportSelectedSections(prev => [...prev, 'answers']);
                } else {
                  setExportSelectedSections(prev =>
                    prev.filter(s => s !== 'answers')
                  );
                }
              }}
            />

            {session?.type === 'exposure' && (
              <Checkbox
                label='Info cibi'
                checked={exportSelectedSections.includes('cibi')}
                onChange={e => {
                  if (e.target.checked) {
                    setExportSelectedSections(prev => [...prev, 'cibi']);
                  } else {
                    setExportSelectedSections(prev =>
                      prev.filter(s => s !== 'cibi')
                    );
                  }
                }}
              />
            )}

            {isExporting && (
              <Text size='sm' c='dimmed' ta='center'>
                Generazione in corso...
              </Text>
            )}
            <Group justify='flex-end' mt='md'>
              <Button
                variant='outline'
                onClick={closeExportModal}
                disabled={isExporting}
              >
                Annulla
              </Button>
              <Button
                onClick={
                  exportFormat === 'csv' ? handleExportCsv : handleExportPdf
                }
                loading={isExporting}
                disabled={isExporting}
              >
                Esporta {exportFormat.toUpperCase()}
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {/* Hidden CSV Links */}
      {session && session.type !== 'body_image' && (
        <>
          <CSVLink
            ref={devicesCsvRef}
            data={devicesCsvData}
            headers={devicesCsvHeaders}
            filename={`sessione_${session.id}_dispositivi_${
              new Date().toISOString().split('T')[0]
            }.csv`}
            style={{ display: 'none' }}
          />
          <CSVLink
            ref={patientCsvRef}
            data={patientCsvData}
            headers={patientCsvHeaders}
            filename={`sessione_${session.id}_utente_${
              new Date().toISOString().split('T')[0]
            }.csv`}
            style={{ display: 'none' }}
          />
          <CSVLink
            ref={sessionCsvRef}
            data={sessionCsvData}
            headers={sessionCsvHeaders}
            filename={`sessione_${session.id}_sessione_${
              new Date().toISOString().split('T')[0]
            }.csv`}
            style={{ display: 'none' }}
          />
          <CSVLink
            ref={eventsCsvRef}
            data={eventsCsvData}
            headers={eventsCsvHeaders}
            filename={`sessione_${session.id}_eventi_${
              new Date().toISOString().split('T')[0]
            }.csv`}
            style={{ display: 'none' }}
          />
          <CSVLink
            ref={phasesCsvRef}
            data={phasesCsvData}
            headers={phasesCsvHeaders}
            filename={`sessione_${session.id}_fasi_${
              new Date().toISOString().split('T')[0]
            }.csv`}
            style={{ display: 'none' }}
          />
          <CSVLink
            ref={answersCsvRef}
            data={answersCsvData}
            headers={getAnswersCsvHeaders(answersCsvData)}
            filename={`sessione_${session.id}_risposte_${
              new Date().toISOString().split('T')[0]
            }.csv`}
            style={{ display: 'none' }}
          />
          {session?.type === 'exposure' && (
            <>
              <CSVLink
                ref={exposureEventsCsvRef}
                data={exposureEventsCsvData}
                headers={exposureEventsCsvHeaders}
                filename={`sessione_${session.id}_eventi_cibi_${
                  new Date().toISOString().split('T')[0]
                }.csv`}
                style={{ display: 'none' }}
              />
              <CSVLink
                ref={exposureFoodStatsCsvRef}
                data={exposureFoodStatsCsvData}
                headers={getExposureItemStatsCsvHeaders(isMultiItem)}
                filename={`sessione_${session.id}_statistiche_cibi_${
                  new Date().toISOString().split('T')[0]
                }.csv`}
                style={{ display: 'none' }}
              />
            </>
          )}
        </>
      )}

      {/* Hidden chart container for PDF generation - always render if events data exists */}
      {session.type !== 'body_image' && chartDataForPdf.length > 0 && (
        <div
          ref={chartContainerRef}
          data-chart-container
          style={{
            position: 'absolute',
            left: '-9999px',
            top: '-9999px',
            width: '800px',
            height: '300px',
            backgroundColor: '#ffffff',
            visibility: 'hidden',
          }}
        >
          <ResponsiveContainer width='100%' height='100%'>
            <RechartsLineChart
              data={chartDataForPdf}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis
                dataKey='time'
                tick={{ fontSize: 10 }}
                label={{
                  value: 'Tempo',
                  position: 'insideBottom',
                  offset: -5,
                  style: { fontSize: 10 },
                }}
              />
              <YAxis
                yAxisId='left'
                tick={{ fontSize: 10 }}
                label={{
                  value: 'HR (bpm)',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 10 },
                }}
              />
              <YAxis
                yAxisId='right'
                orientation='right'
                tick={{ fontSize: 10 }}
                label={{
                  value: 'HRV (ms)',
                  angle: 90,
                  position: 'insideRight',
                  style: { fontSize: 10 },
                }}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 10,
                  backgroundColor: 'var(--mantine-color-default)',
                  color: 'var(--mantine-color-text)',
                }}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {typeof session?.baseline === 'number' && (
                <ReferenceLine
                  y={session.baseline}
                  yAxisId='left'
                  stroke='#ffc658'
                  strokeWidth={2}
                  label={{
                    value: 'Baseline',
                    position: 'top',
                    style: { fontSize: 10 },
                  }}
                />
              )}
              <Line
                yAxisId='left'
                type='monotone'
                dataKey='hr'
                stroke='#8884d8'
                strokeWidth={2}
                dot={(props: {
                  cx?: number;
                  cy?: number;
                  payload?: { feedback?: FeedbackAtPoint[] };
                }) => {
                  const { cx, cy, payload } = props;
                  if (
                    payload?.feedback?.length &&
                    typeof cx === 'number' &&
                    typeof cy === 'number'
                  ) {
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={6}
                        fill='#9333ea'
                        stroke='white'
                        strokeWidth={2}
                      />
                    );
                  }
                  return <g />;
                }}
                name='Heart Rate (bpm)'
              />
              <Line
                yAxisId='right'
                type='monotone'
                dataKey='hrv'
                stroke='#82ca9d'
                strokeWidth={2}
                dot={false}
                name='HRV (ms)'
              />
            </RechartsLineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
